/** Tier-1 requirements = subset of SupportFlowMilestoneId. Attribution is NOT here (tier 2 only, ruling S-4). */
// Must stay a subset of SupportFlowMilestoneId (AM-11). ENFORCER:
// src/modules/support-flow/catalog/activationRequirementIds.assert.ts — a compile-time
// assertion checked by `npm run typecheck:ui`. The jest test
// src/modules/support-flow/__tests__/activationRequirementIds.test.ts documents the rule
// but cannot fail on a type violation (ts-jest runs with diagnostics: false).
export type ActivationRequirementId =
  | 'funnelCreated' | 'domainVerified' | 'published' | 'analyticsConnected'
  | 'stripeConnected' | 'paywallProductsCreated' | 'paywallAttached'
  | 'facebookPixel' | 'tiktokPixel';
export type ActivationAdvisoryId = 'stripeSandbox' | 'productAnalytics' | 'googleAdsGtm';

/** Canonical LIST ORDER of the checklist (core → money → ads); `next` = first open item in this order. */
export const ACTIVATION_REQUIREMENT_IDS: readonly ActivationRequirementId[] = [
  'funnelCreated', 'analyticsConnected', 'published', 'domainVerified',
  'stripeConnected', 'paywallProductsCreated', 'paywallAttached',
  'facebookPixel', 'tiktokPixel',
] as const;

export type IntentGoal = 'first_value' | 'sell';
export type IntentChannel = 'facebook' | 'tiktok' | 'google_ads' | 'organic';
export type IntentProductAnalytics = 'ga4' | 'mixpanel' | 'amplitude' | 'posthog';
export type IntentPlatform = 'web' | 'ios' | 'android';

export const INTENT_GOALS: readonly IntentGoal[] = ['first_value', 'sell'] as const;
export const INTENT_CHANNELS: readonly IntentChannel[] = ['facebook', 'tiktok', 'google_ads', 'organic'] as const;
export const INTENT_PRODUCT_ANALYTICS: readonly IntentProductAnalytics[] = ['ga4', 'mixpanel', 'amplitude', 'posthog'] as const;
export const INTENT_PLATFORMS: readonly IntentPlatform[] = ['web', 'ios', 'android'] as const;

export interface ActivationIntent {
  goal: IntentGoal;
  channels: IntentChannel[];              // [] when goal === 'first_value' or "no ads"
  productAnalytics: IntentProductAnalytics[];
  platform?: IntentPlatform;              // profiling only, never changes the list
  /** Where the intent was last written: the dogfood questionnaire funnel, the CLI, the in-app
   *  intent popover (`app`, contracts §10 AM-16), or never set by a human (`default`). */
  source: 'questionnaire' | 'cli' | 'app' | 'default';
  updatedAt: string;                      // ISO
}
export const DEFAULT_INTENT: Omit<ActivationIntent, 'updatedAt'> = { goal: 'first_value', channels: [], productAnalytics: [], source: 'default' };

/** Firestore: projects/{projectId}/launch/activation — append-only achievements (DC-3). */
export interface ActivationLatchDoc {
  version: 1;
  intent: ActivationIntent;
  achievements: Partial<Record<ActivationRequirementId, { earnedAt: string; evidence: string }>>;
  updatedAt: string;
}

export type ActivationActionClass = 'P' | 'C' | 'D' | 'X';
export interface ActivationAction {
  class: ActivationActionClass;
  label: string;                          // names WHERE it lands (DC-16)
  route?: string;                         // P/C: app path incl. `?guide=<kebab key>` [&action=deploy] [&mode=live]
  dialog?: 'domainDrawer' | 'addIntegration' | 'createFunnel';   // D
  dialogProps?: Record<string, string>;   // e.g. { initialPlatform: 'facebook_pixel' }
  externalUrl?: string;                   // X
  vendorPath?: string[];                  // X: click path shown inline
  articleAlias?: string;                  // article-registry alias
  cli?: string;                           // e.g. 'domains setup <domain>' — null when not automatable
  /** Additive (Plan B): class-D fallback page when no dialog host is mounted. */
  fallbackRoute?: string;
}
export interface ActivationItem {
  id: ActivationRequirementId;
  group: 'core' | 'money' | 'ads';
  earned: boolean;
  earnedAt?: string;
  title: string;                          // past tense when earned, imperative + named object when open
  detail: string;
  action?: ActivationAction;              // exactly one when !earned, absent when earned
  /** Additive (Plan B, AC-056): the evaluator threw — row shows a retry affordance; never counted as earned. */
  checkFailed?: boolean;
}
export interface ActivationAdvisory { id: ActivationAdvisoryId; title: string; detail: string; articleAlias?: string; }
export interface ActivationProjection {
  projectId: string;
  intent: ActivationIntent;
  items: ActivationItem[];                // derived requirement set only (S-5); domain once
  advisories: ActivationAdvisory[];       // outside numerator and denominator
  earnedCount: number;
  totalCount: number;                     // === items.length
  next?: ActivationItem;                  // first open item in list order
  complete: boolean;                      // earnedCount === totalCount
  computedAt: string;
  timingMs: number;                       // server handler time (S-8)
}

/** Tier-2 publish gate finding, appended to the healthcheck body (Plan A adds the field, C renders it). */
export type PublishGateFindingId = 'mode_misalignment' | 'dead_end_screen' | 'stale_enum_option' | 'missing_attribution' | 'not_evaluated';
export interface PublishGateFinding {
  id: PublishGateFindingId;
  severity: 'error' | 'warning' | 'not_evaluated';   // DC-21: only 'error' pauses the deploy
  title: string;
  detail: string;
  actions: ActivationAction[];            // 1, or 2 for two-target findings (DC-16)
  screenId?: string;
}
